import { Injectable } from '@angular/core';
import { Observable } from 'rxjs';
import { HttpClient, HttpParams } from '@angular/common/http';
import { HttpUtilsService } from '../../_base/crud';
import { select, Store } from '@ngrx/store';
import { AppState } from '../../reducers';
import { currentAuthToken } from '../../auth';
import { Allergy } from '../_models/allergy.model';

export enum ViewContext {
  contacts = 'CONTACT',
  patients = 'PATIENT',
}

@Injectable({
  providedIn: 'root',
})
export class AllergiesService {
  private authToken;

  constructor(
    private http: HttpClient,
    private httpUtils: HttpUtilsService,
    private store: Store<AppState>,
  ) {
    this.store.pipe(select(currentAuthToken)).subscribe((res) => (this.authToken = res));
  }

  findAllergies(entityId: number): Observable<any> {
    const params = new HttpParams({ fromString: '_patient=' + entityId + '&_count=' + '&_page=' });
    const headers = this.httpUtils.getHTTPHeaders(this.authToken);
    const url = 'https://aplos2.aidbox.app/AllergyIntolerance';
    return this.http.get<any>(url, { headers, params });
  }

  createAllergy(_allergy: Allergy, entityId) {
    const httpHeaders = this.httpUtils.getHTTPHeaders(this.authToken);
    let onsetValue = null;
    switch (_allergy.onsetType) {
      case 'dateTime': {
        onsetValue = { dateTime: _allergy.onsetDate !== '' ? _allergy.onsetDate : null };
        break;
      }
      case 'string': {
        onsetValue = { string: _allergy.onsetString !== '' ? _allergy.onsetString : null };
        break;
      }
      case 'Period': {
        onsetValue = {
          Period: {
            start: _allergy.onsetPeriodStart !== '' ? _allergy.onsetPeriodStart : null,
            end: _allergy.onsetPeriodEnd !== '' ? _allergy.onsetPeriodEnd : null,
          },
        };
        break;
      }
      case 'Age': {
        onsetValue = {
          Age: {
            value: _allergy.onsetAge !== '' ? _allergy.onsetAge : null,
            unit: _allergy.onsetAgeUnit !== '' ? _allergy.onsetAgeUnit : null,
          },
        };
        break;
      }
      case 'Range': {
        onsetValue = {
          Range: {
            low: { value: _allergy.onsetRangeLow !== '' ? _allergy.onsetRangeLow : null },
            high: { value: _allergy.onsetRangeHigh !== '' ? _allergy.onsetRangeHigh : null },
          },
        };
        break;
      }
      default: {
        break;
      }
    }

    const body = {
      clinicalStatus: {
        coding: [
          {
            code: _allergy.clinicalStatus,
            display: _allergy.clinicalStatus,
          },
        ],
      },
      verificationStatus: {
        coding: [
          {
            code: _allergy.verificationStatus,
            display: _allergy.verificationStatus,
          },
        ],
      },
      type: _allergy.type,
      category: _allergy.category,
      criticality: _allergy.criticality,
      onset: onsetValue,
      code: {
        coding: [
          {
            code: _allergy.code && _allergy.code[`concept`] ? _allergy.code[`concept`][`id`] : null,
            display:
              _allergy.code && _allergy.code[`concept`] && _allergy.code[`concept`][`fsn`]
                ? _allergy.code[`concept`][`fsn`][`term`]
                : null,
          },
        ],
      },
      patient: {
        id: entityId.toString(),
        resourceType: 'Patient',
      },
      recordedDate: _allergy.recordedDate,
      recorder: {
        id:
          _allergy.recorder != null && _allergy.recorder[`id`]
            ? _allergy.recorder[`id`].toString()
            : null,
        resourceType:
          _allergy.recorderResourceType !== '' && _allergy.recorderResourceType !== undefined
            ? _allergy.recorderResourceType
            : null,
      },
      asserter: {
        id:
          _allergy.asserter != null && _allergy.asserter[`id`]
            ? _allergy.asserter[`id`].toString()
            : null,
        resourceType:
          _allergy.asserterType !== '' && _allergy.asserterType !== undefined
            ? _allergy.asserterType
            : null,
      },
      lastOccurrence: _allergy.lastOccurrence,
      note: [
        {
          text: _allergy.note,
        },
      ],
      reaction: [
        {
          substance: {
            coding: [
              {
                code:
                  _allergy.substance && _allergy.substance[`concept`]
                    ? _allergy.substance[`concept`][`id`]
                    : null,
                display:
                  _allergy.substance &&
                  _allergy.substance[`concept`] &&
                  _allergy.substance[`concept`][`fsn`]
                    ? _allergy.substance[`concept`][`fsn`][`term`]
                    : null,
              },
            ],
          },
          manifestation: [
            {
              coding: [
                {
                  code:
                    _allergy.manifestation && _allergy.manifestation[`concept`]
                      ? _allergy.manifestation[`concept`][`id`]
                      : null,
                  display:
                    _allergy.manifestation &&
                    _allergy.manifestation[`concept`] &&
                    _allergy.manifestation[`concept`][`fsn`]
                      ? _allergy.manifestation[`concept`][`fsn`][`term`]
                      : null,
                },
              ],
            },
          ],
          description: _allergy.manifestationDescription,
          onset: _allergy.manifestationOnset,
          severity: _allergy.manifestationSeverity,
          exposureRoute: {
            coding: [
              {
                code:
                  _allergy.exposureRoute && _allergy.exposureRoute[`concept`]
                    ? _allergy.exposureRoute[`concept`][`id`]
                    : null,
                display:
                  _allergy.exposureRoute &&
                  _allergy.exposureRoute[`concept`] &&
                  _allergy.exposureRoute[`concept`][`fsn`]
                    ? _allergy.exposureRoute[`concept`][`fsn`][`term`]
                    : null,
              },
            ],
          },
        },
      ],
    };
    return this.http.post<Allergy>('https://aplos2.aidbox.app/AllergyIntolerance', body, {
      headers: httpHeaders,
      observe: 'response',
    });
  }

  updateAllergy(_allergy: Allergy, entityId): Observable<Allergy> {
    const httpHeaders = this.httpUtils.getHTTPHeaders(this.authToken);
    let onsetValue = null;
    switch (_allergy.onsetType) {
      case 'dateTime': {
        onsetValue = { dateTime: _allergy.onsetDate !== '' ? _allergy.onsetDate : null };
        break;
      }
      case 'string': {
        onsetValue = { string: _allergy.onsetString !== '' ? _allergy.onsetString : null };
        break;
      }
      case 'Period': {
        onsetValue = {
          Period: {
            start: _allergy.onsetPeriodStart !== '' ? _allergy.onsetPeriodStart : null,
            end: _allergy.onsetPeriodEnd !== '' ? _allergy.onsetPeriodEnd : null,
          },
        };
        break;
      }
      case 'Age': {
        onsetValue = {
          Age: {
            value: _allergy.onsetAge !== '' ? _allergy.onsetAge : null,
            unit: _allergy.onsetAgeUnit !== '' ? _allergy.onsetAgeUnit : null,
          },
        };
        break;
      }
      case 'Range': {
        onsetValue = {
          Range: {
            low: { value: _allergy.onsetRangeLow !== '' ? _allergy.onsetRangeLow : null },
            high: { value: _allergy.onsetRangeHigh !== '' ? _allergy.onsetRangeHigh : null },
          },
        };
        break;
      }
      default: {
        break;
      }
    }
    const body = {
      clinicalStatus: {
        coding: [
          {
            code: _allergy.clinicalStatus,
            display: _allergy.clinicalStatus,
          },
        ],
      },
      verificationStatus: {
        coding: [
          {
            code: _allergy.verificationStatus,
            display: _allergy.verificationStatus,
          },
        ],
      },
      type: _allergy.type,
      category: _allergy.category,
      criticality: _allergy.criticality,
      onset: onsetValue,
      code: {
        coding: [
          {
            code: _allergy.code && _allergy.code[`concept`] ? _allergy.code[`concept`][`id`] : null,
            display:
              _allergy.code && _allergy.code[`concept`] && _allergy.code[`concept`][`fsn`]
                ? _allergy.code[`concept`][`fsn`][`term`]
                : null,
          },
        ],
      },
      patient: {
        id: entityId.toString(),
        resourceType: 'Patient',
      },
      recordedDate: _allergy.recordedDate,
      recorder: {
        id:
          _allergy.recorder != null && _allergy.recorder[`id`]
            ? _allergy.recorder[`id`].toString()
            : null,
        resourceType:
          _allergy.recorderResourceType !== '' && _allergy.recorderResourceType !== undefined
            ? _allergy.recorderResourceType
            : null,
      },
      asserter: {
        id:
          _allergy.asserter != null && _allergy.asserter[`id`]
            ? _allergy.asserter[`id`].toString()
            : null,
        resourceType:
          _allergy.asserterType !== '' && _allergy.asserterType !== undefined
            ? _allergy.asserterType
            : null,
      },
      lastOccurrence: _allergy.lastOccurrence,
      note: [
        {
          text: _allergy.note,
        },
      ],
      reaction: [
        {
          substance: {
            coding: [
              {
                code:
                  _allergy.substance && _allergy.substance[`concept`]
                    ? _allergy.substance[`concept`][`id`]
                    : null,
                display:
                  _allergy.substance &&
                  _allergy.substance[`concept`] &&
                  _allergy.substance[`concept`][`fsn`]
                    ? _allergy.substance[`concept`][`fsn`][`term`]
                    : null,
              },
            ],
          },
          manifestation: [
            {
              coding: [
                {
                  code:
                    _allergy.manifestation && _allergy.manifestation[`concept`]
                      ? _allergy.manifestation[`concept`][`id`]
                      : null,
                  display:
                    _allergy.manifestation &&
                    _allergy.manifestation[`concept`] &&
                    _allergy.manifestation[`concept`][`fsn`]
                      ? _allergy.manifestation[`concept`][`fsn`][`term`]
                      : null,
                },
              ],
            },
          ],
          description: _allergy.manifestationDescription,
          onset: _allergy.manifestationOnset,
          severity: _allergy.manifestationSeverity,
          exposureRoute: {
            coding: [
              {
                code:
                  _allergy.exposureRoute && _allergy.exposureRoute[`concept`]
                    ? _allergy.exposureRoute[`concept`][`id`]
                    : null,
                display:
                  _allergy.exposureRoute &&
                  _allergy.exposureRoute[`concept`] &&
                  _allergy.exposureRoute[`concept`][`fsn`]
                    ? _allergy.exposureRoute[`concept`][`fsn`][`term`]
                    : null,
              },
            ],
          },
        },
      ],
    };

    const url = 'https://aplos2.aidbox.app/AllergyIntolerance/' + _allergy.id;
    return this.http.put<Allergy>(url, body, { headers: httpHeaders });
  }

  deleteAllergy(id?: number) {
    const httpHeaders = this.httpUtils.getHTTPHeaders(this.authToken);
    return this.http.delete<any>('https://aplos2.aidbox.app/AllergyIntolerance/' + id, {
      headers: httpHeaders,
    });
  }
}
